/**
 * Executive Reports Index — hub listing all strategic executive reports.
 *
 * Currently:
 *   • Autonomous Manufacturing Blueprint (Smart Factory Uetze) → /report
 *   • NeuNexus Health Intelligence (Uetze Health Campus) → /renalix
 *
 * As new executive reports are produced (e.g. Enterprise Platform, Renewable
 * Energy Program), simply extend the REPORTS array.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Factory, HeartPulse, FileText, Printer, Download } from "lucide-react";
import { LanguageSwitcher, useT } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  border:  "rgba(255,255,255,0.08)",
  glass:   "rgba(255,255,255,0.045)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  amber:   "#F59E0B",
  teal:    "#14B8A6",
  rose:    "#F43F5E",
  purple:  "#A855F7",
};

// Reports catalog. Cover art uses radial gradients keyed by domain accent.
const REPORTS = [
  {
    id: "autonomous",
    icon: Factory,
    accent: "#F59E0B",
    accent2: "#A855F7",
    href: "/report",
    // language-aware fields resolved at render time
    eyebrow: { de: "Strategische Vision · Fertigung", en: "Strategic Vision · Manufacturing", pt: "Visão Estratégica · Fabricação" },
    title:   { de: "Der Bauplan für autonome Fertigung.",         en: "The Autonomous Manufacturing Blueprint.", pt: "O Blueprint da Manufatura Autónoma." },
    sub:     {
      de: "Das strategische Dokument, das den Weg von NeuNexus zur intelligenten und autonomen Fertigung definiert — kognitive Robotik, humanoide Automatisierung und industrielle KI, konvergent in einer einzigen Plattform.",
      en: "The strategic document defining NeuNexus' path to intelligent and autonomous manufacturing — cognitive robotics, humanoid automation and industrial AI, converged on a single platform.",
      pt: "O documento estratégico que define o caminho da NeuNexus rumo à manufatura inteligente e autónoma — robótica cognitiva, automação humanoide e IA industrial, convergentes numa única plataforma.",
    },
    meta:    {
      de: ["15 Seiten · Executive Deck", "Kognitive Robotik · Humanoide · Industrie 4.0", "Vorgeschlagen: NEURA Robotics · Fraunhofer"],
      en: ["15 pages · Executive deck",  "Cognitive Robotics · Humanoids · Industry 4.0", "Proposed: NEURA Robotics · Fraunhofer"],
      pt: ["15 páginas · Deck executivo", "Robótica Cognitiva · Humanoides · Indústria 4.0", "Propostos: NEURA Robotics · Fraunhofer"],
    },
    cta:     { de: "Executive Report lesen", en: "Read the Executive Report", pt: "Ler o Relatório Executivo" },
    pdfs:    [
      { url: "/downloads/The_Autonomous_Manufacturing_Blueprint.pdf", size: "18.9 MB",
        label: { de: "Blueprint (PDF)", en: "Blueprint (PDF)", pt: "Blueprint (PDF)" } },
    ],
  },
  {
    id: "health",
    icon: HeartPulse,
    accent: "#14B8A6",
    accent2: "#F43F5E",
    href: "/renalix",
    eyebrow: { de: "Strategische Vision · Gesundheit", en: "Strategic Vision · Health", pt: "Visão Estratégica · Saúde" },
    title:   { de: "NeuNexus Health Intelligence.",                             en: "NeuNexus Health Intelligence.",                            pt: "NeuNexus Health Intelligence." },
    sub:     {
      de: "Uetze Health Innovation Campus · Renalix · prädiktive Nieren-Intelligenz.",
      en: "Uetze Health Innovation Campus · Renalix · predictive kidney intelligence.",
      pt: "Uetze Health Innovation Campus · Renalix · inteligência renal preditiva.",
    },
    meta:    {
      de: ["Phygital-Architektur", "AI Core Engine", "Renalix Care & Pro", "Fahrplan 2024–2028+"],
      en: ["Phygital architecture", "AI core engine",  "Renalix Care & Pro",  "Roadmap 2024–2028+"],
      pt: ["Arquitetura phygital",  "AI core engine",  "Renalix Care & Pro",  "Roadmap 2024–2028+"],
    },
    cta:     { de: "Bericht öffnen", en: "Open report", pt: "Abrir relatório" },
    pdfs:    [
      { url: "/downloads/NeuNexus_Health_Campus.pdf", size: "18.4 MB",
        label: { de: "Health Campus (PDF)", en: "Health Campus (PDF)", pt: "Health Campus (PDF)" } },
      { url: "/downloads/Predictive_Renal_Healthcare_Blueprint.pdf", size: "17.0 MB",
        label: { de: "Predictive Renal Healthcare — Research Blueprint (PDF)",
                 en: "Predictive Renal Healthcare — Research Blueprint (PDF)",
                 pt: "Predictive Renal Healthcare — Blueprint de Investigação (PDF)" } },
    ],
  },
];

const TXT = {
  de: {
    back:   "BSGOTECH",
    header: "Executive Strategiebericht",
    eyebrow: "Strategische Executive Reports",
    title:  "Die strategischen Berichte des NeuNexus-Ökosystems.",
    subtitle: "Investor- und stakeholderfähige Dokumente. Weitere Berichte werden hier veröffentlicht, sobald sie erstellt sind.",
    empty:  "Weitere Berichte in Kürze.",
    docCenter: "Alle Dokumente im Operations Center öffnen",
  },
  en: {
    back:   "BSGOTECH",
    header: "Executive Strategic Reports",
    eyebrow: "Strategic Executive Reports",
    title:  "The strategic reports of the NeuNexus ecosystem.",
    subtitle: "Investor- and stakeholder-grade documents. Additional reports are published here as they are produced.",
    empty:  "More reports coming soon.",
    docCenter: "Open all documents in the Operations Center",
  },
  pt: {
    back:   "BSGOTECH",
    header: "Relatórios Estratégicos Executivos",
    eyebrow: "Relatórios Estratégicos Executivos",
    title:  "Os relatórios estratégicos do ecossistema NeuNexus.",
    subtitle: "Documentos com nível investidor/stakeholder. Novos relatórios são publicados aqui à medida que forem produzidos.",
    empty:  "Mais relatórios em breve.",
    docCenter: "Abrir todos os documentos no Operations Center",
  },
};

export default function ReportsIndex() {
  const { lang } = useT();
  const L = TXT[lang] || TXT.en;

  React.useEffect(() => {
    document.title = `${L.header} · BSGOTECH`;
  }, [L.header]);

  return (
    <div className="relative min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="page-reports-index">
      <LanguageSwitcher />

      {/* Top bar */}
      <div className="sticky top-0 z-40" style={{ background: "rgba(8,11,18,0.85)", borderBottom: `1px solid ${P.border}`, backdropFilter: "blur(12px)" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }} data-testid="reports-back-home">
            <ChevronRight size={13} className="rotate-180" />
            <span className="uppercase tracking-[0.28em]">{L.back}</span>
          </Link>
          <div className="hidden md:block text-[11px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
            {L.header}
          </div>
          <div />
        </div>
      </div>

      {/* Header */}
      <header className="border-b" style={{ borderColor: P.border }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>{L.eyebrow}</div>
          <h1 className="mt-6 font-semibold tracking-tight text-[32px] sm:text-[42px] lg:text-[54px] leading-[1.05] max-w-4xl" style={{ color: "#fff", letterSpacing: "-0.015em" }}>
            {L.title}
          </h1>
          <p className="mt-5 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>{L.subtitle}</p>
        </div>
      </header>

      {/* Reports grid */}
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14 space-y-4">
        {REPORTS.map((r) => {
          const Ic = r.icon;
          return (
            <Link key={r.id} to={r.href} data-testid={`reports-card-${r.id}`}
              className="group block rounded-xl overflow-hidden transition hover:border-white/25"
              style={{
                background: `linear-gradient(135deg, ${r.accent}14 0%, ${P.bg2} 55%, ${r.accent2}18 100%)`,
                border: `1px solid ${r.accent}55`,
              }}>
              <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0">
                <div className="p-8 lg:p-10">
                  <div className="flex items-center gap-2">
                    <Ic size={16} style={{ color: r.accent }} strokeWidth={1.75} />
                    <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: r.accent }}>
                      {r.eyebrow[lang] || r.eyebrow.en}
                    </div>
                  </div>
                  <h2 className="mt-4 font-semibold tracking-tight text-[24px] lg:text-[32px] leading-[1.1]" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
                    {r.title[lang] || r.title.en}
                  </h2>
                  <p className="mt-3 max-w-xl text-[13px] leading-relaxed" style={{ color: P.dim }}>
                    {r.sub[lang] || r.sub.en}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {(r.meta[lang] || r.meta.en).map((m, i) => (
                      <span key={i}
                        className="text-[10.5px] uppercase tracking-[0.2em] px-2.5 py-1 rounded"
                        style={{ background: "rgba(255,255,255,0.04)", color: P.silver, border: `1px solid ${P.border}` }}>
                        {m}
                      </span>
                    ))}
                  </div>
                  <div className="mt-7 inline-flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: r.accent }}>
                    <FileText size={13} />
                    {r.cta[lang] || r.cta.en}
                    <ArrowUpRight size={13} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                  {r.pdfs && r.pdfs.length > 0 && (
                    <div className="mt-3 ml-6 flex flex-col gap-1.5">
                      {r.pdfs.map((doc, di) => (
                        <a
                          key={di}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`reports-pdf-${r.id}-${di}`}
                          className="inline-flex items-center gap-2 text-[11.5px] transition hover:text-white"
                          style={{ color: P.silver }}
                        >
                          <Download size={12} />
                          {doc.label[lang] || doc.label.en} <span style={{ opacity: 0.5 }}>· {doc.size}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative min-h-[220px] lg:min-h-[100%] flex items-center justify-center overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${P.bg} 0%, ${r.accent}22 55%, ${r.accent2}33 100%)`, borderLeft: `1px solid ${P.border}` }}
                  aria-hidden="true">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 30% 30%, ${r.accent}33 0%, transparent 55%), radial-gradient(circle at 75% 70%, ${r.accent2}33 0%, transparent 55%)`,
                  }} />
                  <div className="relative w-24 h-24 lg:w-28 lg:h-28 rounded-full flex items-center justify-center"
                    style={{ background: `${r.accent}22`, border: `1px solid ${r.accent}66`, boxShadow: `0 0 40px ${r.accent}44` }}>
                    <Ic size={44} style={{ color: r.accent }} strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}

        <div className="pt-6">
          <Link to="/ops" data-testid="reports-doc-center-link"
            className="inline-flex items-center gap-2 text-[12px] font-semibold" style={{ color: P.purple }}>
            {L.docCenter} <ArrowUpRight size={12} />
          </Link>
        </div>
      </main>
    </div>
  );
}
